import { Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button, IconButton } from '../primitives/button.js';

// ───────── FormSection ─────────

export interface FormSectionProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/** Titled group for long forms (fieldset + legend for assistive tech). */
export function FormSection({ title, description, children, className }: FormSectionProps) {
  return (
    <fieldset className={cn('grid min-w-0 gap-3 border-t border-border-subtle pt-5 first:border-t-0 first:pt-0', className)}>
      <legend className="sr-only">{title}</legend>
      <div className="grid gap-0.5" aria-hidden>
        <h3 className="text-body font-semibold text-text-primary">{title}</h3>
        {description ? <p className="text-helper text-text-secondary">{description}</p> : null}
      </div>
      {children}
    </fieldset>
  );
}

// ───────── RepeatableList ─────────

export interface RepeatableListProps<T> {
  items: readonly T[];
  onChange: (items: T[]) => void;
  /** Render one row's fields. `update` replaces the row. */
  renderItem: (item: T, index: number, update: (next: T) => void) => React.ReactNode;
  /** New empty row added by the add button. */
  createItem: () => T;
  addLabel: string;
  emptyText?: React.ReactNode;
  /** Maximum number of rows (add button disabled at the limit). */
  max?: number;
  /** Accessible name of a row, used for the remove button ("Quitar <itemLabel>"). */
  itemLabel?: (item: T, index: number) => string;
  disabled?: boolean;
  className?: string;
}

/** Editable list of structured rows (add / remove), e.g. price ranges or materials. Presentational only. */
export function RepeatableList<T>({
  items,
  onChange,
  renderItem,
  createItem,
  addLabel,
  emptyText = 'Sin elementos.',
  max,
  itemLabel = (_item, i) => `elemento ${i + 1}`,
  disabled,
  className,
}: RepeatableListProps<T>) {
  const atMax = max !== undefined && items.length >= max;
  return (
    <div className={cn('grid gap-3', className)}>
      {items.length === 0 ? <p className="text-helper text-text-secondary">{emptyText}</p> : null}
      {items.length > 0 ? (
        <ul className="grid gap-3">
          {items.map((item, i) => (
            <li key={i} className="flex items-start gap-2 rounded-menu border border-border-subtle bg-surface-subtle p-3">
              <div className="grid min-w-0 flex-1 gap-3">
                {renderItem(item, i, (next) => onChange(items.map((x, j) => (j === i ? next : x))))}
              </div>
              {!disabled ? (
                <IconButton
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 aria-hidden />}
                  label={`Quitar ${itemLabel(item, i)}`}
                  onClick={() => onChange(items.filter((_, j) => j !== i))}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {!disabled ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" size="sm" leadingIcon={<Plus aria-hidden />} disabled={atMax} onClick={() => onChange([...items, createItem()])}>
            {addLabel}
          </Button>
          {max !== undefined ? (
            <span className="cm-tabular text-helper text-text-secondary">
              {items.length} de {max}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
